import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useGetProductDetail } from '../../common/utils/hooks/useGetProductDetail'
import { isOfferExpired } from '../../common/utils/isOfferExpired'
import { ViewPrivateOffer } from '../PrivateOfferForm/ViewPrivateOffer/ViewPrivateOffer'
import React, { useEffect, useMemo } from 'react'
import { useCurrentCloudProvider } from '@labrav/common'
import { useParams } from 'react-router-dom'
import { getCurrentPrivateOffer } from '../../modules/privateOffer/action'
import { OfferStatus, PrivateOffer } from '../../modules/privateOffer/reducer'
import { useDispatch, useSelector } from '../../store/hooks'
import { isEmpty } from 'lodash'
import { PlanType, StepperComponent } from '@labrav/react-components'
import { useStyles } from './NewPrivateOffer.styles'
import { ContainerPricingModel } from '../../modules/productsListing/reducer'
import { useAccountId } from '@labrav/common'

export { isOfferExpired }

export const stepsForPrivateOffer = ['Creation', 'Sent', 'Viewed', 'Accepted']

export const NewPrivateOfferWrapper: React.FC<{
  crmOpportunityId?: string
  externalBaseURL?: string
}> = ({ crmOpportunityId, externalBaseURL }) => {
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const partnerId = useSelector<string>(
    state => state.AccountData.user.partnerData?.partnerId || ''
  )
  const selectedCloudType = useCurrentCloudProvider()
  const {
    cloud: routeCloud,
    productId,
    privateOfferId,
    accountId: routeAccountId,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    privateOfferId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const currentAccountId = routeAccountId || fallbackAccountId
  const cloudMarketplace =
    (routeCloud?.toUpperCase() as typeof selectedCloudType) || selectedCloudType
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  // Loads the product list on HubSpot GCP details, where it is not already in the store.
  const loadedProduct = useGetProductDetail({
    accountId: currentAccountId,
    productId: productId || '',
    cloudMarketplace,
  })
  const selectedProduct =
    products.find(productItem => productItem.productId === productId) ||
    loadedProduct
  const privateOffer = useSelector<PrivateOffer>(
    state => state.privateOffers.currentPrivateOffer[privateOfferId!]
  )
  const productType = selectedProduct?.productType || PlanType.SAAS_CONTRACT
  const contractPricingModel = selectedProduct?.containerPricingModel as
    | ContainerPricingModel
    | undefined

  useEffect(() => {
    if (
      !isEmpty(currentAccountId) &&
      !isEmpty(privateOfferId) &&
      isEmpty(privateOffer)
    ) {
      dispatch(
        getCurrentPrivateOffer(
          currentAccountId,
          privateOfferId!,
          cloudMarketplace,
          productId,
          crmOpportunityId
        )
      )
    }
  }, [
    currentAccountId,
    privateOfferId,
    privateOffer,
    cloudMarketplace,
    productId,
    crmOpportunityId,
    dispatch,
  ])

  const activeStep = useMemo(() => {
    return getActiveStep(privateOffer?.offerStatus)
  }, [privateOffer?.offerStatus])

  const isExpired = useMemo(() => {
    return (
      !isEmpty(privateOffer) &&
      isOfferExpired(
        new Date(privateOffer?.offerExpirationDate),
        privateOffer?.offerStatus
      )
    )
  }, [privateOffer?.offerStatus, privateOffer?.offerExpirationDate])

  const shouldShowStepperComponent =
    privateOffer?.offerStatus === OfferStatus?.PRIVATE_OFFER_FAILED ||
    isExpired ||
    privateOffer?.offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
    privateOffer?.offerStatus ===
      OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS

  return isEmpty(privateOffer) ? (
    <div data-testid="empty-private-offer-wrapper"></div>
  ) : (
    <div className={classes.stepperWrapper}>
      {shouldShowStepperComponent ? (
        <></>
      ) : (
        <div data-testid="stepperDiv">
          {' '}
          <StepperComponent
            activeStep={activeStep}
            steps={stepsForPrivateOffer}
          />
        </div>
      )}

      <ViewPrivateOffer
        privateOffer={privateOffer}
        planType={productType}
        externalBaseURL={externalBaseURL}
        contractPricingModel={contractPricingModel}
      />
    </div>
  )
}

export const getActiveStep = (status: OfferStatus) => {
  switch (status) {
    case OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS:
      return 1
    case OfferStatus.PRIVATE_OFFER_EMAIL_SENT:
      return 2
    case OfferStatus.PRIVATE_OFFER_VIEWED:
      return 3
    case OfferStatus.PRIVATE_OFFER_ACCEPTED:
      return 4
    default:
      return 0
  }
}
